<template>
    <li class="zs-tree-item" :class="{ 'has-children': hasChildren, expanded: isExpanded }">
        <div
            class="zs-tree-node"
            :class="{ selected: isSelected, selectable: canSelectCurrentNode }"
            @click="handleNodeClick"
        >
            <div class="zs-tree-expander" @click.stop="toggleExpand">
                <slot
                    name="expander"
                    :node="node"
                    :depth="depth"
                    :isExpanded="isExpanded"
                    :hasChildren="hasChildren"
                    :hasSelectedChildren="hasSelectedChildren"
                >
                    <ZSIcon
                        v-if="hasChildren"
                        :icon="expanderIcon"
                        :rotation="isExpanded ? '180' : '90'"
                        size="extra-small"
                        color="var(--color-orange)"
                    />
                </slot>
            </div>

            <div v-if="showIcons" class="zs-tree-icon">
                <slot
                    name="icon"
                    :node="node"
                    :depth="depth"
                    :isExpanded="isExpanded"
                    :isSelected="isSelected"
                    :hasSelectedChildren="hasSelectedChildren"
                >
                    <ZSIcon :icon="computedNodeIcon" size="small" :color="computedNodeIconColor" />
                </slot>
            </div>

            <div v-if="showCheckboxes && canSelectCurrentNode" class="zs-tree-checkbox" @click.stop>
                <ZSCheckBox
                    label=""
                    :modelValue="isSelected"
                    :indeterminate="hasSelectedChildren && !isSelected"
                    @update:modelValue="toggleCheckboxSelection"
                />
            </div>

            <div class="zs-tree-label">
                <slot
                    :node="node"
                    :depth="depth"
                    :isExpanded="isExpanded"
                    :isSelected="isSelected"
                    :hasSelectedChildren="hasSelectedChildren"
                >
                    {{ label }}
                </slot>
            </div>
        </div>

        <transition name="zs-tree-slide">
            <ul v-if="hasChildren && isExpanded" class="zs-tree-children">
                <ZSTreeItem
                    v-for="child in node[itemChildren]"
                    ref="childItems"
                    :key="child[itemKey]"
                    :node="child"
                    :item-key="itemKey"
                    :item-label="itemLabel"
                    :item-value="itemValue"
                    :item-children="itemChildren"
                    :selectable="selectable"
                    :is-selectable="isSelectable"
                    :multiselect="multiselect"
                    :model-value="modelValue"
                    :show-checkboxes="showCheckboxes"
                    :show-icons="showIcons"
                    :node-icon="nodeIcon"
                    :node-icon-color="nodeIconColor"
                    :expander-icon="expanderIcon"
                    :depth="depth + 1"
                    :initially-expanded="initiallyExpanded"
                    @update:model-value="$emit('update:modelValue', $event)"
                    @node-click="$emit('node-click', $event)"
                    @node-expand="$emit('node-expand', $event)"
                >
                    <template #default="slotProps">
                        <slot v-bind="slotProps"></slot>
                    </template>
                    <template #expander="slotProps">
                        <slot name="expander" v-bind="slotProps" />
                    </template>
                    <template #icon="slotProps">
                        <slot name="icon" v-bind="slotProps" />
                    </template>
                </ZSTreeItem>
            </ul>
        </transition>
    </li>
</template>

<script setup>
import { computed, ref } from 'vue';

import ZSCheckBox from '../form/ZSCheckBox.vue';
import ZSIcon from '../global/ZSIcon.vue';

const props = defineProps({
    node: {
        type: Object,
        required: true
    },
    itemKey: {
        type: String,
        default: 'id'
    },
    itemLabel: {
        type: [String, Function],
        default: 'label'
    },
    itemValue: {
        type: [String, Function],
        default: 'id'
    },
    itemChildren: {
        type: String,
        default: 'children'
    },
    selectable: {
        type: Boolean,
        default: false
    },
    isSelectable: {
        type: Function,
        default: null
    },
    multiselect: {
        type: Boolean,
        default: false
    },
    modelValue: {
        type: [Object, String, Number, Array],
        default: null
    },
    showCheckboxes: {
        type: Boolean,
        default: false
    },
    showIcons: {
        type: Boolean,
        default: false
    },
    nodeIcon: {
        type: [String, Function],
        default: ''
    },
    nodeIconColor: {
        type: String,
        default: ''
    },
    expanderIcon: {
        type: String,
        default: 'Chevron'
    },
    depth: {
        type: Number,
        default: 0
    },
    initiallyExpanded: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'node-click', 'node-expand']);

const isExpanded = ref(props.initiallyExpanded);

const hasChildren = computed(() => {
    // Allow a node to declare itself expandable before its children are loaded
    // (e.g. lazy-loaded branches). Opt-in via an explicit `hasChildren` flag.
    if (props.node.hasChildren === true) {
        return true;
    }
    return Boolean(props.node[props.itemChildren] && props.node[props.itemChildren].length > 0);
});

const canSelectNode = (node, currentDepth) => {
    if (!props.selectable) {
        return false;
    }

    if (props.isSelectable) {
        return props.isSelectable(node, currentDepth);
    }

    return true;
};

const canSelectCurrentNode = computed(() => canSelectNode(props.node, props.depth));

const resolveNodeValue = (node, currentDepth) => {
    if (typeof props.itemValue === 'function') {
        return props.itemValue(node, currentDepth);
    }

    return node[props.itemValue];
};

const label = computed(() => {
    if (typeof props.itemLabel === 'function') {
        return props.itemLabel(props.node, props.depth);
    }

    return props.node[props.itemLabel];
});

const nodeValue = computed(() => resolveNodeValue(props.node, props.depth));

const isSelected = computed(() => {
    if (!canSelectCurrentNode.value || props.modelValue === null || props.modelValue === undefined) {
        return false;
    }

    if (props.multiselect) {
        return Array.isArray(props.modelValue) && props.modelValue.includes(nodeValue.value);
    }

    return props.modelValue === nodeValue.value;
});

const hasSelectedChildren = computed(() => {
    if (!hasChildren.value || props.modelValue === null || props.modelValue === undefined) {
        return false;
    }

    const checkSelected = (nodes, currentDepth = props.depth + 1) => {
        return nodes.some((childNode) => {
            if (canSelectNode(childNode, currentDepth)) {
                const childValue = resolveNodeValue(childNode, currentDepth);
                const selected = props.multiselect
                    ? Array.isArray(props.modelValue) && props.modelValue.includes(childValue)
                    : props.modelValue === childValue;

                if (selected) {
                    return true;
                }
            }

            if (childNode[props.itemChildren] && childNode[props.itemChildren].length > 0) {
                return checkSelected(childNode[props.itemChildren], currentDepth + 1);
            }

            return false;
        });
    };

    return checkSelected(props.node[props.itemChildren]);
});

const computedNodeIcon = computed(() => {
    if (props.node.icon) {
        return props.node.icon;
    }

    if (typeof props.nodeIcon === 'function') {
        return props.nodeIcon(props.node, props.depth);
    }

    if (props.nodeIcon) {
        return props.nodeIcon;
    }

    return hasChildren.value ? 'Folder' : 'File';
});

const computedNodeIconColor = computed(() => {
    if (props.node.iconColor) {
        return props.node.iconColor;
    }

    if (props.nodeIconColor) {
        return props.nodeIconColor;
    }

    return 'var(--color-blue)';
});

const handleNodeClick = () => {
    emit('node-click', props.node);

    if (canSelectCurrentNode.value) {
        if (props.multiselect) {
            const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
            const index = currentValues.indexOf(nodeValue.value);

            if (index > -1) {
                currentValues.splice(index, 1);
            } else {
                currentValues.push(nodeValue.value);
            }

            emit('update:modelValue', currentValues);
            return;
        }

        emit('update:modelValue', nodeValue.value);
        return;
    }

    if (hasChildren.value) {
        toggleExpand();
    }
};

const toggleCheckboxSelection = () => {
    if (!canSelectCurrentNode.value) {
        return;
    }

    if (props.multiselect) {
        const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
        const index = currentValues.indexOf(nodeValue.value);

        if (index > -1) {
            currentValues.splice(index, 1);
        } else {
            currentValues.push(nodeValue.value);
        }

        emit('update:modelValue', currentValues);
        return;
    }

    emit('update:modelValue', isSelected.value ? null : nodeValue.value);
};

const toggleExpand = () => {
    if (hasChildren.value) {
        const willExpand = !isExpanded.value;
        isExpanded.value = willExpand;
        if (willExpand) {
            emit('node-expand', props.node);
        }
    }
};

const expandNode = () => {
    if (hasChildren.value && !isExpanded.value) {
        isExpanded.value = true;
        emit('node-expand', props.node);
    }
};

const collapseNode = () => {
    isExpanded.value = false;
};

defineExpose({
    expandNode,
    collapseNode
});
</script>

<style scoped>
.zs-tree-item {
    position: relative;
    margin: 0;
    padding: 0;
    list-style: none;
}

.zs-tree-node {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 12px;
    cursor: default;
    border-radius: 4px;
    transition: background-color 0.2s ease;
}

.zs-tree-node:hover {
    background-color: var(--color-very-light-gray-alt);
}

.zs-tree-node.selectable {
    cursor: pointer;
}

.zs-tree-node.selected {
    font-weight: 600;
    color: var(--color-orange-alt);
    background-color: var(--color-orange-highlight);
}

.zs-tree-expander {
    display: flex;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.zs-tree-icon {
    display: flex;
    align-items: center;
    justify-content: center;
}

.zs-tree-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
}

.zs-tree-checkbox :deep(.checkbox-wrapper) {
    padding: 0;
}

.zs-tree-label {
    flex: 1;
    overflow: hidden;
    font-size: 0.95rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.zs-tree-children {
    --zs-tree-line-color: rgba(148, 163, 184, 0.75);
    margin: 0;
    margin-left: 8px;
    padding: 0 0 0 24px;
    position: relative;
}

.zs-tree-children::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 20px;
    left: 9px;
    width: 1px;
    background: var(--zs-tree-line-color);
}

.zs-tree-children > .zs-tree-item::before {
    content: '';
    position: absolute;
    top: 20px;
    left: -15px;
    width: 15px;
    height: 1px;
    background: var(--zs-tree-line-color);
}

.zs-tree-children > .zs-tree-item:last-child::after {
    content: '';
    position: absolute;
    top: 20px;
    bottom: 0;
    left: -15px;
    width: 1px;
    background: var(--color-white);
}

.zs-tree-slide-enter-active,
.zs-tree-slide-leave-active {
    overflow: hidden;
    max-height: 500px;
    transition: all 0.3s ease;
}

.zs-tree-slide-enter-from,
.zs-tree-slide-leave-to {
    opacity: 0;
    max-height: 0;
}
</style>
